import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch, store } from '../store';
import { updateError } from './user';
import { getFilter, handleLoading } from '../api/practiceMessageApis';
import { getPracticeId } from './clincheck';

const initialState = {
    isLoading: false,
    practiceMessage: { results: [] },
    imageLoader: false,
    messageDetail: {},
    practiceMessageSequences: { results: [] },
    specificPracticeMessageSequences: {},
    templateDetails: { results: [] },
    templateDetailsList: [],
    userTemplateDetails: {},
    deleteTemplateDetails: { results: [] },
    message: '',
    error: '',
    isAutoSaving: false,
    autoSaveStatus: false,
    subjectTemplateList: [],
    showSubjectList: false,
    loadingForTemplate: false,
    search: null,
    myEngagementTemplates: { results: [], count: 0, isLoading: false },
    myWhatsappTemplates: { results: [], count: 0, isLoading: false },
};

const slice = createSlice({
    name: 'practiceMessage',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
            state.message = '';
            state.messageDetail = {};
            state.userTemplateDetails = {};
        },
        endLoading(state) {
            state.isLoading = false;
        },
        getPracticeSequences(state, action) {
            state.isLoading = false;
            state.practiceMessageSequences.results = [];
            state.practiceMessage = action.payload;
        },
        BlankSequence(state) {
            state.isLoading = false;
            state.practiceMessageSequences.results = [];
        },
        getPracticeSequenceMessage(state, action) {
            state.isLoading = false;
            state.practiceMessageSequences = action.payload;
        },
        getSpecificPracticeSequenceMessage(state, action) {
            state.isLoading = false;
            state.specificPracticeMessageSequences = action.payload;
        },
        getMessageDetail(state, action) {
            state.isLoading = false;
            state.messageDetail = action.payload;
        },
        getTemplate(state, action) {
            state.isLoading = false;
            state.templateDetails = action.payload;
        },
        updateTemplateDetailsList(state, action) {
            state.templateDetailsList = action.payload;
        },
        addTemplate(state, action) {
            state.isLoading = false;
            state.addTemplateDetails = action.payload;
        },
        getUserTemplate(state, action) {
            state.isLoading = false;
            state.userTemplateDetails = action.payload;
        },
        getUpdateData(state) {
            state.isLoading = false;
            state.message = 'Template updated successFully';
        },
        deleteTemplate(state) {
            state.isLoading = false;
            state.deleteTemplateDetails = 'Deleted';
            state.message = 'Template deleted successFully';
        },
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },
        clearMessage(state) {
            state.message = '';
            state.error = '';
        },
        updateMessages(state, action) {
            state.isLoading = false;
            state.practiceMessageSequences.results = action.payload;
        },
        updateSequenceData(state, action) {
            state.isLoading = false;
            state.practiceMessage.results = action.payload;
        },
        imageLoaderUpdate(state, action) {
            state.imageLoader = action.payload;
        },
        setIsAutoSaving(state, action) {
            state.isAutoSaving = action.payload;
        },
        setIsAutoSaveStatus(state, action) {
            state.autoSaveStatus = action.payload;
        },
        updateSubjectTemplateList(state, action) {
            state.subjectTemplateList = action.payload;
        },
        updateShowSubjectList(state, action) {
            state.showSubjectList = action.payload;
        },
        setLoadingForTemplate(state, action) {
            state.loadingForTemplate = action.payload;
        },
        setSearchTemplate(state, action) {
            state.search = action.payload;
        },
        setMyEngagementTemplatesLoading(state, action) {
            state.myEngagementTemplates.isLoading = action.payload;
        },
        setMyEngagementTemplates(state, action) {
            state.myEngagementTemplates.results = action.payload?.results || [];
            state.myEngagementTemplates.count = action.payload?.count || 0;
            state.myEngagementTemplates.isLoading = false;
        },
        setMyWhatsappTemplatesLoading(state, action) {
            state.myWhatsappTemplates.isLoading = action.payload;
        },
        setMyWhatsappTemplates(state, action) {
            state.myWhatsappTemplates.results = action.payload?.results || [];
            state.myWhatsappTemplates.count = action.payload?.count || 0;
            state.myWhatsappTemplates.isLoading = false;
        },
    }
});

// Reducer
export default slice.reducer;

export const {
    clearMessage,
    reset,
    updateSubjectTemplateList,
    updateShowSubjectList,
    setSearchTemplate,
    startLoading,
    updateTemplateDetailsList,
    hasError,
    endLoading,
    setLoadingForTemplate,
    getTemplate,
    deleteTemplate,
    getUserTemplate,
    BlankSequence,
    setIsAutoSaving,
    getUpdateData,
    setIsAutoSaveStatus
} = slice.actions;

// Get Practice Sequences List
export function getPracticeSequencesList(id) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${id}/sequences/`);
            if (response.status === 200) {
                dispatch(slice.actions.getPracticeSequences(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getSpecificPracticeSequence(id, seqId) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${id}/sequences/${seqId}/`);
            if (response.status === 200) {
                dispatch(slice.actions.getSpecificPracticeSequenceMessage(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getPracticeSequenceMessageList(id, seqId) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${id}/sequences/${seqId}/messages/`);
            if (response.status === 200) {
                dispatch(slice.actions.getPracticeSequenceMessage(response.data));
            } else {
                dispatch(slice.actions.getPracticeSequenceMessage(initialState.practiceMessageSequences));
                dispatch(slice.actions.hasError(response.data.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getPracticeMessageDetail(practiceId, sequenceId, messageId) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/sequences/${sequenceId}/messages/${messageId}/`);
            if (response.status === 200) {
                dispatch(slice.actions.getMessageDetail(response.data));
            } else {
                dispatch(updateError({
                    message: response.data.detail || 'Something want wrong',
                    success: false,
                }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(updateError({
                message: error.message || 'Something want wrong',
                success: false,
            }));
        }
    };
}

export function updateMessageDetail({ sequenceId, messageId, detail, callback }) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/sequences/${sequenceId}/messages/${messageId}/`, 'patch', detail);
            if (response.status === 200) {
                const { practiceMessageSequences: { results } } = store.getState().practiceMessage;
                if (results.length > 0) {
                    const data = [];
                    results.forEach(message => {
                        let messageDetail = { ...message };
                        if (messageDetail.id === messageId) {
                            messageDetail = { ...messageDetail, ...detail };
                        }
                        data.push(messageDetail);
                    });
                    dispatch(slice.actions.updateMessages(data));
                }
                callback({ success: true, message: 'Message detail updated successfully' }, true);
            } else {
                callback({ success: false, message: response.data }, true);
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(updateError({
                message: error.message || 'Something want wrong',
                success: false
            }));
            if (callback) {
                callback({
                    message: error.message || 'Something went wrong',
                    success: false,
                }, true);
            }
        }
    };
}

export function sendTestMail({ sequenceId, messageId, detail, callback }) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/sequences/${sequenceId}/messages/${messageId}/send_test/`, 'put', detail);
            if (response.status === 200) {
                callback({ success: true, message: 'Test email send successfully' }, true);
            } else {
                callback({ success: false, message: response.data }, true);
            }
        } catch (error) {
            dispatch(updateError(error || 'Something want wrong'));
        }
    };
}

export function updateSequenceDetail({ sequenceId, detail, callback }) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const response = await callApi(`/api/v1/practices/${practiceId}/sequences/${sequenceId}/`, 'patch', detail);
            if (response.status === 200) {
                const { practiceMessage: { results } } = store.getState().practiceMessage;
                const data = [];
                results.forEach(message => {
                    let messageDetail = { ...message };
                    if (messageDetail.id === sequenceId) {
                        messageDetail = { ...messageDetail, ...detail };
                    }
                    data.push(messageDetail);
                });
                dispatch(slice.actions.updateSequenceData(data));
                callback({
                    message: 'Sequence detail updated successfully',
                    success: true,
                }, true);
            } else {
                callback({ success: false, message: response.detail }, true);
            }
        } catch (error) {
            dispatch(updateError({
                message: error || 'Something want wrong',
                success: false
            }));
        }
    };
}

export function uploadImage(detail, callback, quill) {
    return async () => {
        try {
            dispatch(slice.actions.imageLoaderUpdate(true));
            const { practiceId } = store.getState().practiceTreatmentList;
            const response = await callApi(`/api/v1/practices/${practiceId}/media/`, 'post', detail);
            if (response.status === 201) {
                callback({ success: true, data: response.data, quill });
            } else {
                callback({ success: false, message: response.data });
            }
            dispatch(slice.actions.imageLoaderUpdate(false));
        } catch (error) {
            dispatch(slice.actions.imageLoaderUpdate(false));
            dispatch(updateError({ message: error.message || 'Something want wrong', success: false }));
        }
    };
}

// Aggregation endpoints — returns templates across every practice the caller has access to.
// When the caller has access to only one practice, results are limited to that practice.
// Used by the message-templates page to present a single list for multi-practice admins.
export function getMyEngagementTemplates({ practiceIn, category, search, limit = 1000, offset = 0, ordering = '-created_at' } = {}) {
    return async () => {
        dispatch(slice.actions.setMyEngagementTemplatesLoading(true));
        try {
            const params = new URLSearchParams();
            if (practiceIn?.length) params.set('practice__in', practiceIn.join(','));
            if (category) params.set('category', category);
            if (search) params.set('search', search);
            params.set('channel', 'EMAIL');
            params.set('limit', String(limit));
            params.set('offset', String(offset));
            params.set('ordering', ordering);
            const response = await callApi(`/api/v1/me/engagement-templates/?${params.toString()}`);
            if (response.ok) {
                dispatch(slice.actions.setMyEngagementTemplates(response.data));
            } else {
                dispatch(slice.actions.setMyEngagementTemplatesLoading(false));
                // User-initiated page load: surface the failure rather than hide it.
                dispatch(updateError({
                    message: `Failed to load templates across practices (status ${response.status ?? 'unknown'}).`,
                    success: false,
                }));
            }
        } catch (error) {
            dispatch(slice.actions.setMyEngagementTemplatesLoading(false));
            dispatch(updateError({
                message: `Failed to load templates across practices: ${error?.message || 'network error'}`,
                success: false,
            }));
        }
    };
}

export function getMyWhatsappTemplates({ practiceIn, status, search, limit = 1000, offset = 0, ordering = '-created_at' } = {}) {
    return async () => {
        dispatch(slice.actions.setMyWhatsappTemplatesLoading(true));
        try {
            const params = new URLSearchParams();
            if (practiceIn?.length) params.set('practice__in', practiceIn.join(','));
            if (status) params.set('status', status);
            if (search) params.set('search', search);
            params.set('limit', String(limit));
            params.set('offset', String(offset));
            params.set('ordering', ordering);
            const response = await callApi(`/api/v1/me/whatsapp-templates/?${params.toString()}`);
            if (response.ok) {
                dispatch(slice.actions.setMyWhatsappTemplates(response.data));
            } else {
                dispatch(slice.actions.setMyWhatsappTemplatesLoading(false));
                dispatch(updateError({
                    message: `Failed to load WhatsApp templates across practices (status ${response.status ?? 'unknown'}).`,
                    success: false,
                }));
            }
        } catch (error) {
            dispatch(slice.actions.setMyWhatsappTemplatesLoading(false));
            dispatch(updateError({
                message: `Failed to load WhatsApp templates across practices: ${error?.message || 'network error'}`,
                success: false,
            }));
        }
    };
}

// Build targeting query string from filter object.
// Accepted keys: forLeadStatus (string[]), forTreatmentCategories (number[]),
// forTreatmentItems (number[]).  All are optional; empty/absent keys are
// omitted so the BE applies no filter for that dimension.
function buildTargetingQuery(targetingFilters) {
    if (!targetingFilters) return '';
    let q = '';
    const { forLeadStatus, forTreatmentCategories, forTreatmentItems } = targetingFilters;
    if (forLeadStatus && forLeadStatus.length > 0) {
        q += `&for_lead_status=${forLeadStatus.join(',')}`;
    }
    if (forTreatmentCategories && forTreatmentCategories.length > 0) {
        q += `&for_treatment_categories=${forTreatmentCategories.join(',')}`;
    }
    if (forTreatmentItems && forTreatmentItems.length > 0) {
        q += `&for_treatment_items=${forTreatmentItems.join(',')}`;
    }
    return q;
}

// Get Practice Template List
// targetingFilters: optional { forLeadStatus: string[], forTreatmentCategories: number[], forTreatmentItems: number[] }
export function getTemplateList(id, removeUnLayer, unLayerFilter, noLoading, targetingFilters) {
    return async () => {
        if (!noLoading) {
            dispatch(slice.actions.startLoading());
            handleLoading(true);
        }
        try {
            const query = removeUnLayer ? '&use_unlayer=false' : '';
            const unLayerFilterQuery = getFilter(unLayerFilter);
            const targetingQuery = buildTargetingQuery(targetingFilters);
            const response = await callApi(`/api/v1/practices/${id}/engagement-templates/?ordering=-created_at${query}${unLayerFilterQuery}${targetingQuery}`);
            if (response.status === 200) {
                dispatch(slice.actions.getTemplate(response.data));

            } else {
                dispatch(slice.actions.hasError(response.data.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
        finally {
            if (!noLoading) {
                handleLoading(false);
            }
        }
    };
}

export function getUserTemplateDetails(practiceId, id) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/engagement-templates/${id}/`);
            if (response.status === 200) {
                dispatch(slice.actions.getUserTemplate(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data.detail || 'Something went wrong'));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function addTemplateList({ id, details, callback, isDuplicate = false }) {
    return async () => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await callApi(`/api/v1/practices/${id}/engagement-templates/`, 'post', details);
            if (response.status === 201) {
                dispatch(slice.actions.addTemplate(response.data));
                callback({
                    message: isDuplicate ? 'Template duplicated successfully' : 'Template added successfully',
                    success: true,
                    data: response.data,
                }, true);
            } else {
                dispatch(slice.actions.hasError((response.data) || 'Something went wrong'));
                callback({
                    message: response.data || 'Something went wrong',
                    success: false
                }, true);
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            if (callback) {
                callback({
                    message: error.message || 'Something went wrong',
                    success: false,
                }, true);
            }
        }
    };
}

export function getTemplateHistory({ practiceId, templateId, callback }) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/engagement-templates/${templateId}/history/`);
            if (response.status === 200) {
                callback(response.data);
            } else {
                callback({ results: [] });
                dispatch(updateError({ message: 'Failed to load version history', success: false }));
            }
        } catch (error) {
            callback({ results: [] });
            dispatch(updateError({ message: error?.message || 'Failed to load version history', success: false }));
        }
    };
}

export function revertTemplate({ practiceId, templateId, historyId, callback }) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/engagement-templates/${templateId}/revert/`,
                'post',
                { history_id: historyId }
            );
            if (response.status === 200) {
                if (callback) callback(response.data);
            } else {
                dispatch(updateError({ message: 'Failed to restore version', success: false }));
                if (callback) callback({ success: false, message: response.data?.detail || 'Failed to restore version' });
            }
        } catch (error) {
            dispatch(updateError({ message: error?.message || 'Failed to restore version', success: false }));
            if (callback) callback({ success: false, message: error?.message || 'Failed to restore version' });
        }
    };
}
